<script>
    import { onMount } from "svelte";

    import PatientEncounterPatientDocuments from "./PatientEncounterPatientDocuments.svelte";

    import {
        CardLayout,
        RichTextEditor,
    } from "../../../Components/Common/index.svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { editIcon } from "../../../Utils/utilConstants.svelte";

    import { getText, showError } from "../../../Utils/utilFunctions.svelte";

    export let patientDetails = {},
        patientEncounter = {},
        isInEdit = false,
        reloadPatientDetails = () => {};

    $: btnOptions[1].isHidden = isInEdit;

    const btnOptions = [
        {
            iconName: "addIcon",
            hasTooltip: false,
            tooltip: "Upload document",
            iFont: "fa-solid fa-upload",
            iconFunction: () => _openUploadFileModal(),
            btnText: "Upload document",
        },
        {
            iconName: "editNoteIcon",
            iFont: editIcon,
            iconFunction: () => _toggleEditMode(),
            hasTooltip: false,
            btnText: "Edit",
        },
    ];

    let backupPatientDetails = {},
        backupPatientEncounter = {},
        isUpdatingNotes = false;

    let isUploadFileModalOpen = false;

    onMount(() => {
        updateBackupDetails();
    });

    export const updateNotes = async () => {
        isUpdatingNotes = true;

        await updatePatientDetails();

        if (backupPatientEncounter.notes != patientEncounter.notes)
            await updatePatientEncounterNotes();

        await reloadPatientDetails();

        updateBackupDetails();

        isUpdatingNotes = false;

        _toggleEditMode();
    };

    const updatePatientDetails = async () => {
        await apiMethods.putMethod
            .updatePatient(patientDetails)
            .then(async (res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update patient details, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    const updatePatientEncounterNotes = async () => {
        await apiMethods.putMethod
            .updatePatientEncounter(patientEncounter, false, "Update encounter note")
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update procedure specific notes, please refresh the page and try again and contact support if the issue persists.",
                    );
            })
            .catch((err) => showError(err));
    };

    export const discardChanges = () => {
        _toggleEditMode();
        patientDetails = {
            ...patientDetails,
            badDebtor: backupPatientDetails.badDebtor,
            badDebtorByMedicare: backupPatientDetails.badDebtorByMedicare,
            notes: backupPatientDetails.notes,
            alerts: backupPatientDetails.alerts,
        };
        patientEncounter = { ...backupPatientEncounter };
    };

    const updateBackupDetails = () => {
        backupPatientDetails = { ...patientDetails };
        backupPatientEncounter = { ...patientEncounter };
    };

    const handleReloadAfterFileChanges = async () => {
        if (!isInEdit) await reloadPatientDetails();
        else {
            updateBackupDetails();

            await reloadPatientDetails();

            let tempPatientNotes = patientDetails.notes;
            let tempPatientAlerts = patientDetails.alerts;
            let tempEncounterNotes = patientEncounter.notes;

            if (
                backupPatientDetails.notes != tempPatientNotes ||
                backupPatientDetails.alerts != tempPatientAlerts
            ) {
                patientDetails.notes = backupPatientDetails.notes;
                patientDetails.alerts = backupPatientDetails.alerts;
                backupPatientDetails.notes = tempPatientNotes;
                backupPatientDetails.alerts = tempPatientAlerts;
            }

            if (backupPatientEncounter.notes != tempEncounterNotes) {
                patientEncounter.notes = backupPatientEncounter.notes;
                backupPatientEncounter.notes = tempEncounterNotes;
            }
        }
    };

    // HELPER FUNCTIONS
    const _toggleEditMode = () => (isInEdit = !isInEdit);

    const _openUploadFileModal = () => (isUploadFileModalOpen = true);
</script>

<CardLayout title="Alerts and Documents" {btnOptions}>
    <div class="flex flex-col flex-1 items-center gap-4 px-2 py-4 w-full">
        <div class="flex flex-1 gap-x-12 w-full">
            <div class="flex gap-x-4">
                <label for="deceased" class="cursor-pointer"> Deceased </label>
                <input
                    type="checkbox"
                    id="deceased"
                    class="toggle toggle-secondary"
                    disabled={!isInEdit}
                    bind:checked={patientDetails.deceased}
                />
            </div>

            <div class="flex gap-x-4">
                <label for="badDebtor" class="cursor-pointer">
                    Bad Debtor
                </label>
                <input
                    type="checkbox"
                    id="badDebtor"
                    class="toggle toggle-secondary"
                    disabled={!isInEdit}
                    bind:checked={patientDetails.badDebtor}
                />
            </div>

            {#if backupPatientDetails.badDebtorByMedicare}
                <div class="flex flex-1 gap-x-4">
                    <label
                        for="badDebtorByMedicare"
                        class="title cursor-pointer"
                    >
                        Bad Debtor recognised by Medicare Number
                    </label>
                    <input
                        type="checkbox"
                        id="badDebtorByMedicare"
                        class="toggle toggle-secondary"
                        disabled={!isInEdit}
                        bind:checked={patientDetails.badDebtorByMedicare}
                    />
                </div>
            {/if}
        </div>

        <!-- <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title font-bold underline">Patient Notes:</p>
            {#if isInEdit}
                <textarea
                    class="textarea textarea-bordered w-full"
                    rows="4"
                    bind:value={patientDetails.notes}
                />
            {:else}
                <div
                    class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 whitespace-pre-line min-h-8"
                >
                    {getText(patientDetails.notes)}
                </div>
            {/if}
        </div> -->

        <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title text-error font-bold underline">Patient Alerts:</p>
            <!-- {#if isInEdit}
                <textarea
                    class="textarea textarea-bordered w-full"
                    rows="4"
                    bind:value={patientDetails.alerts}
                />
            {:else}
                <div
                    class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 whitespace-pre-line min-h-8"
                >
                    {getText(patientDetails.alerts)}
                </div>
            {/if} -->
            <RichTextEditor
                bind:content={patientDetails.alerts}
                config={{ isDisabled: !isInEdit }}
            />
        </div>

        <!-- <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title font-bold underline">Encounter notes:</p>
            {#if isInEdit}
                <textarea
                    class="textarea textarea-bordered w-full"
                    rows="4"
                    bind:value={patientEncounter.notes}
                />
            {:else}
                <div
                    class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 whitespace-pre-line min-h-8"
                >
                    {getText(patientEncounter.notes)}
                </div>
            {/if}
        </div> -->

        <div class="flex flex-col flex-1 w-full gap-y-2">
            <p class="title font-bold">Patient Documents:</p>

            <div
                class="flex flex-1 w-full border border-2 rounded-lg px-2 py-1 leading-8 min-h-8"
            >
                <PatientEncounterPatientDocuments
                    bind:isUploadFileModalOpen
                    {patientDetails}
                    doctorId={patientEncounter.doctorId}
                    syncPatientFiles={handleReloadAfterFileChanges}
                />
            </div>
        </div>

        {#if isInEdit}
            <div class="flex flex-1 w-full items-center justify-end gap-x-4">
                <button
                    class="btn btn-sm btn-error w-30"
                    disabled={isUpdatingNotes}
                    on:click|preventDefault={discardChanges}
                >
                    Cancel
                </button>
                <button
                    class="btn btn-sm btn-secondary w-30 {isUpdatingNotes
                        ? 'loading'
                        : ''}"
                    disabled={isUpdatingNotes}
                    on:click|preventDefault={updateNotes}
                >
                    {isUpdatingNotes ? "Saving" : "Save"}
                </button>
            </div>
        {/if}
    </div>
</CardLayout>

<style>
    .title {
        width: 200px;
    }
</style>
